// First-touch acquisition attribution for paid/organic landings.
//
// GA4 ↔ Google Ads auto-tagging already attributes the CLICK (gclid) for GA4's
// campaign reports, and PostHog auto-captures utm_*/gclid on its own $pageview.
// What neither captures is the APP-SPECIFIC first-touch context: which piece of
// public proof — a video tour or a whitepaper — the visitor actually landed on
// before they signed up. We capture that once (first touch wins), persist it in
// localStorage so it survives the mobile OAuth full-page REDIRECT (100% of paid
// clicks are mobile), and attach it to the sign_up / signed_up conversion so
// funnels can answer "which proof converts." Compact + non-PII by construction
// (content slugs + campaign params only — never document/essay/profile text).

import { EMAIL_MEASUREMENT_STARTS } from '../data/policyNotice';

const KEY = 'stratia_attribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

// Map the landing URL to a compact content id: the video tour or paper the
// visitor arrived on, else the bare page. This is the "which proof" dimension.
function landingContent(pathname, params) {
    if (pathname.startsWith('/resources/')) {
        return `paper:${pathname.slice('/resources/'.length)}`;
    }
    if (pathname === '/resources') return 'page:/resources';
    return `page:${pathname || '/'}`;
}

/**
 * Capture first-touch attribution once per browser. Idempotent: a later browse
 * (e.g. video tour → landing hero) never overwrites the original landing, so the
 * proof that actually pulled the visitor in is the one credited. Call as early
 * as possible on app mount, before any sign-in can complete.
 */
export function captureLanding() {
    try {
        if (localStorage.getItem(KEY)) return;   // first touch wins
        const url = new URL(window.location.href);
        const params = url.searchParams;
        const attr = {
            landing_content: landingContent(url.pathname, params),
            landing_path: url.pathname,
        };
        for (const k of UTM_KEYS) {
            const v = params.get(k);
            if (v) attr[k] = v.slice(0, 100);
        }
        const gclid = params.get('gclid');
        if (gclid) attr.gclid = gclid.slice(0, 120);
        // The referring site's DOMAIN only (#2997): an organic search, a
        // LinkedIn post or a partner page. Never the path or query, which can
        // carry a search term; our own origin is not a referrer.
        const ref = referringDomain(document.referrer, url.origin);
        if (ref) attr.referring_domain = ref;
        attr.landed_on = new Date().toISOString().slice(0, 10);
        localStorage.setItem(KEY, JSON.stringify(attr));
    } catch { /* storage unavailable / SSR — attribution is best-effort */ }
}

/** 'https://www.linkedin.com/feed/...' -> 'linkedin.com'; '' for none, for
 * a non-http referrer, or for our own origin (an in-app hop is not a source). */
export function referringDomain(referrer, ownOrigin) {
    if (!referrer || typeof referrer !== 'string') return '';
    try {
        const u = new URL(referrer);
        if (!/^https?:$/.test(u.protocol)) return '';
        if (ownOrigin && u.origin === ownOrigin) return '';
        return u.hostname.replace(/^www\./, '').slice(0, 80);
    } catch {
        return '';
    }
}

// Email first touches are held back until per-address email measurement is
// allowed: the day the Privacy Policy that discloses it went live (one
// definition, data/policyNotice.js, pinned to the desk senders' date guard).
// Before it an email landing is recorded on the person as "email" with no
// campaign, which says nothing per-send.
export const EMAIL_MEASUREMENT_FROM = EMAIL_MEASUREMENT_STARTS;

const today_ = () => new Date().toISOString().slice(0, 10);

/** Whether per-address email measurement is allowed yet (the notice date). */
export function emailMeasurementAllowed(today = today_()) {
    return today >= EMAIL_MEASUREMENT_FROM;
}

/** The stored attribution as the sign-in events may carry it: an email
 * landing loses its campaign tags until EMAIL_MEASUREMENT_FROM (security
 * review, #2997: the sign-in events attached them per person). */
export function heldAttribution(attr = getAttribution(), today = today_()) {
    if (!attr || typeof attr !== 'object') return {};
    if (attr.utm_medium !== 'email' || emailMeasurementAllowed(today)) return attr;
    const out = { ...attr };
    for (const k of ['utm_source', 'utm_campaign', 'utm_term', 'utm_content']) delete out[k];
    return out;
}

const FIRST_TOUCH_FIELDS = ['landing_content', 'landing_path', 'referring_domain', 'landed_on',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

/**
 * The first-touch facts as they may be stored against a signed-in person
 * (PostHog $set_once person properties and the users/{email} record), from
 * the stored landing. `gclid` becomes a boolean: whether the first visit was
 * a paid click is the acquisition fact; the click id itself identifies an
 * ad interaction and stays out of the person record. {} when nothing stored.
 */
export function firstTouchFacts(attr = getAttribution(), today = new Date().toISOString().slice(0, 10)) {
    if (!attr || typeof attr !== 'object' || !attr.landing_path) return {};
    const out = {};
    for (const k of FIRST_TOUCH_FIELDS) {
        const v = attr[k];
        if (typeof v === 'string' && v) out[k] = v.slice(0, 120);
    }
    out.paid_click = !!attr.gclid;
    if (out.utm_medium === 'email' && !emailMeasurementAllowed(today)) {
        for (const k of ['utm_source', 'utm_campaign', 'utm_term', 'utm_content']) delete out[k];
    }
    return out;
}

/** firstTouchFacts as PostHog person properties, each prefixed first_touch_. */
export function firstTouchPersonProps(facts = firstTouchFacts()) {
    const out = {};
    for (const [k, v] of Object.entries(facts || {})) out[`first_touch_${k}`] = v;
    return out;
}

/** The stored first-touch attribution, flattened for event props ({} if none). */
export function getAttribution() {
    try {
        const raw = localStorage.getItem(KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

// ── Email-link landing (#2408, the opportunity engine) ──────────────────────
// Our emails tag every link utm_source/utm_medium=email/utm_campaign=<id>
// (compose.utm): a campaign id, never an address. When the visitor lands from
// one, the campaign is held for this tab only, and AnalyticsListener fires
// `email_link_landed {campaign}` right after identify, so the click is tied
// to the signed-in person (their hashed id), not an anonymous browser. A
// signed-out landing waits in sessionStorage until they sign in; it never
// fires before identify.
const EMAIL_LANDING_KEY = 'stratia_email_landing';
const CAMPAIGN_RE = /^[A-Za-z0-9._-]{1,80}$/;

/** Hold this page's email campaign, if it came from one of our emails. */
export function captureEmailLanding() {
    try {
        const url = new URL(window.location.href);
        const p = url.searchParams;
        const campaign = p.get('utm_campaign') || '';
        if (p.get('utm_medium') !== 'email' || !CAMPAIGN_RE.test(campaign)) return null;
        const landing = {
            campaign,
            source: CAMPAIGN_RE.test(p.get('utm_source') || '') ? p.get('utm_source') : null,
            // A path never carries an address (the server refuses the same).
            path: /@|%40/i.test(url.pathname) ? null : url.pathname.slice(0, 120),
        };
        sessionStorage.setItem(EMAIL_LANDING_KEY, JSON.stringify(landing));
        return landing;
    } catch {
        return null;
    }
}

/** The held landing, removed so it fires once ({campaign, source, path} or null). */
export function takeEmailLanding() {
    try {
        const raw = sessionStorage.getItem(EMAIL_LANDING_KEY);
        if (!raw) return null;
        sessionStorage.removeItem(EMAIL_LANDING_KEY);
        const l = JSON.parse(raw);
        return l && CAMPAIGN_RE.test(String(l.campaign || '')) ? l : null;
    } catch {
        return null;
    }
}
